import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Title,
  Loader,
  Text,
  Collapse,
  Box,
  Modal,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconArrowRight, IconCheck, IconChevronDown } from "@tabler/icons-react"
import _ from "lodash"
import { useState, useEffect } from "react"
import { Navigate, Outlet, useLocation } from "react-router-dom"

import {
  OnboardingResponses,
  StepsConfig,
} from "@costory/types/endpoints/onboard"
import { OnboardingStatus } from "@costory/types/prisma-client"

import { REFRESH_INTERVAL_MS } from "@costory/shared/const"

import DynamicIcon from "@costory/front/components/icons/DynamicIcon"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { AddBillingDataSourceFunnel } from "@costory/front/pages/Datasources/BillingDatasourcesPage"
import {
  useOnboardStatusQuery,
  useSkipStep,
} from "@costory/front/queries/onboard"

type Props = {
  status: OnboardingResponses.Status
}

const OnboardingStatusPage = ({ status }: Props) => {
  const setupStepsCompleted = _(status.setupSteps)
    .filter((step) => step.completed)
    .size()
  const totalSetupSteps = _.size(
    status.setupSteps.filter((step) => !step.skipped),
  )

  // Auto-collapse Setup and expand Getting Started when setup is completed
  const isSetupCompleted =
    setupStepsCompleted === totalSetupSteps && totalSetupSteps > 0
  const [setupOpen, setSetupOpen] = useState(!isSetupCompleted)
  const [onboardingOpen, setOnboardingOpen] = useState(isSetupCompleted)

  // Update section states when setup completion status changes
  useEffect(() => {
    setSetupOpen(!isSetupCompleted)
    setOnboardingOpen(isSetupCompleted)
  }, [isSetupCompleted])

  const onboardingStepsCompleted = _(status.onboardingSteps)
    .filter((step) => step.completed)
    .size()
  const totalOnboardingSteps = _.size(
    status.onboardingSteps.filter((step) => !step.skipped),
  )

  const [
    isOpen,
    { open: openImportDatasourceModal, close: closeImportDatasourceModal },
  ] = useDisclosure(false)

  return (
    <Box w="100%" p="md">
      <Stack gap="lg">
        <Group
          align="center"
          justify="space-between"
          style={{ cursor: "pointer" }}
          onClick={() => setSetupOpen(!setupOpen)}
        >
          <Group>
            <Title order={3}>Setup</Title>
            <Badge variant="light">
              {setupStepsCompleted}/{totalSetupSteps} completed
            </Badge>
          </Group>
          <ActionIcon variant="subtle" size="sm">
            <IconChevronDown
              style={{
                transform: setupOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </ActionIcon>
        </Group>

        <Collapse in={setupOpen}>
          <Stack gap="sm">
            {status.setupSteps.map((step, index) => (
              <RenderStepCard
                key={step.name}
                step={step}
                index={index}
                status={status.onboardingStatus}
                fullStatus={status}
                onOpenImportDatasource={openImportDatasourceModal}
              />
            ))}
          </Stack>
        </Collapse>

        <Group
          align="center"
          justify="space-between"
          style={{
            cursor:
              status.onboardingStatus === OnboardingStatus.ONBOARDING
                ? "pointer"
                : "not-allowed",
            opacity:
              status.onboardingStatus === OnboardingStatus.ONBOARDING ? 1 : 0.5,
          }}
          onClick={() =>
            status.onboardingStatus === OnboardingStatus.ONBOARDING &&
            setOnboardingOpen(!onboardingOpen)
          }
        >
          <Group>
            <Title order={3}>Getting Started</Title>
            <Badge variant="light">
              {onboardingStepsCompleted}/{totalOnboardingSteps} completed
            </Badge>
          </Group>
          <ActionIcon variant="subtle" size="sm">
            <IconChevronDown
              style={{
                cursor:
                  status.onboardingStatus === OnboardingStatus.ONBOARDING
                    ? "pointer"
                    : "not-allowed",
                transform: onboardingOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </ActionIcon>
        </Group>
        <Collapse in={onboardingOpen}>
          <Stack gap="sm">
            {status.onboardingSteps.map((step, index) => (
              <RenderStepCard
                key={step.name}
                step={step}
                index={index}
                status={status.onboardingStatus}
                fullStatus={status}
                onOpenImportDatasource={openImportDatasourceModal}
              />
            ))}
          </Stack>
        </Collapse>
      </Stack>
      <Modal
        opened={isOpen}
        onClose={closeImportDatasourceModal}
        size="70vw"
        classNames={{ content: "custom-modal-content" }}
        title={<Title order={2}>Import new billing datasource</Title>}
      >
        <Modal.Body h="70vh">
          <AddBillingDataSourceFunnel
            onFinish={closeImportDatasourceModal}
            redirectPage="datasources/billing"
          />
        </Modal.Body>
      </Modal>
    </Box>
  )
}

const RenderStepCard = ({
  step,
  index,
  fullStatus,
  onOpenImportDatasource,
}: {
  step: StepsConfig
  index: number
  status: OnboardingStatus
  fullStatus: OnboardingResponses.Status
  onOpenImportDatasource: () => void
}) => {
  const skipStepMutation = useSkipStep()
  const canSkip =
    !step.completed &&
    (step.name === "Metrics and Events" || step.name === "Connect Slack")
  // Check if billing datasource exists to enable Ingest Native Labels
  const billingStep = fullStatus.setupSteps.find(
    (s) => s.name === "Add Billing Datasource",
  )
  const hasBillingDatasource = !!billingStep?.completed
  // If billing is processing, disable Ingest Native Labels until processing ends
  const isBillingProcessing = !!billingStep?.processing
  const isDisabled =
    (step.name === "Ingest Native Labels" &&
      (!hasBillingDatasource || isBillingProcessing)) ||
    false

  if (step.skipped) return null

  return (
    <Card key={index} p="md" style={{ opacity: isDisabled ? 0.5 : 1 }}>
      <Group justify="space-between" align="flex-start">
        <Group align="flex-start" style={{ flex: 1 }}>
          <ActionIcon
            variant="light"
            color={
              step.processing
                ? "blue"
                : step.completed
                  ? "green"
                  : isDisabled
                    ? "gray"
                    : "primary"
            }
            size="lg"
          >
            {step.processing ? (
              <Loader size="sm" />
            ) : step.completed ? (
              <IconCheck />
            ) : (
              <DynamicIcon iconName={step.icon} />
            )}
          </ActionIcon>
          <Stack>
            <Group align="left">
              <Title order={5}>{step.name}</Title>
              {step.required ? (
                <Badge variant="light" color="red">
                  Required
                </Badge>
              ) : (
                <Badge variant="light" color="yellow">
                  Recommended
                </Badge>
              )}
              {step.completed && (
                <Badge variant="light" color="green">
                  Completed
                </Badge>
              )}
            </Group>
            <Text size="sm" c="dimmed">
              {step.description}
            </Text>
          </Stack>
        </Group>
        <Group gap="xs">
          {canSkip && (
            <Button
              variant="subtle"
              size="xs"
              loading={skipStepMutation.isPending}
              onClick={() => skipStepMutation.mutate(step.name)}
            >
              Skip
            </Button>
          )}
          {!isDisabled && !step.completed && (
            <Group justify="right">
              {step.name === "Add Billing Datasource" ? (
                <ActionIcon
                  onClick={onOpenImportDatasource}
                  variant="light"
                  color="primary"
                >
                  <IconArrowRight />
                </ActionIcon>
              ) : step.link ? (
                <ActionIcon
                  component="a"
                  href={step.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="light"
                  color="primary"
                >
                  <IconArrowRight />
                </ActionIcon>
              ) : null}
            </Group>
          )}
        </Group>
      </Group>
    </Card>
  )
}

export const OnboardingGuard = () => {
  const onboardStatus = useOnboardStatusQuery(REFRESH_INTERVAL_MS)
  return (
    <QueryWrapper query={onboardStatus}>
      {({ data: status }) => <OnboardingGuardChild status={status} />}
    </QueryWrapper>
  )
}

const OnboardingGuardChild = ({
  status,
}: {
  status: OnboardingResponses.Status
}) => {
  const location = useLocation()

  if (status.onboardingStatus === OnboardingStatus.SETUP) {
    if (!location.pathname.includes("/onboarding")) {
      return <Navigate to="/onboarding" replace />
    }
    return <OnboardingStatusPage status={status} />
  }
  // If onboarding is not needed, render the actual route content
  return <Outlet />
}

export const OnboardingPage = () => {
  const onboardStatus = useOnboardStatusQuery(REFRESH_INTERVAL_MS)
  return (
    <QueryWrapper query={onboardStatus}>
      {({ data: status }) => <OnboardingStatusPage status={status} />}
    </QueryWrapper>
  )
}
